import { useCallback, useEffect, useState } from 'react'
import { FgHelp } from '../help/FgHelp'
import { connectApi, type Checklist, type ChecklistStepKey } from './connectApi'

/** Where each step is done. Anchors point into /connect; the rest are their own pages. */
const STEP_ACTIONS: Record<ChecklistStepKey, { label: string; href: string }> = {
  connect_books: { label: 'Connect QuickBooks', href: '/' },
  invite_team: { label: 'Open your firm', href: '/' },
  connect_ai: { label: 'Connect Claude or ChatGPT', href: '/connect#connect-claude' },
  install_excel: { label: 'Install for Excel', href: '/connect#connect-excel' },
  first_playbook_demo: { label: 'Practice a playbook', href: '/playbooks' },
  first_playbook_real: { label: 'Pick a playbook', href: '/playbooks' },
}

export function GettingStartedChecklist({ api = connectApi }: { api?: Pick<typeof connectApi, 'checklist' | 'markStep'> }) {
  const [checklist, setChecklist] = useState<Checklist | null>(null)
  const [error, setError] = useState<string | null>(null)
  const load = useCallback(() => { api.checklist().then(setChecklist, () => setError('Your checklist could not be loaded.')) }, [api])
  useEffect(load, [load])

  const mark = async (step: ChecklistStepKey) => {
    try { setChecklist(await api.markStep(step)) } catch { setError('That step could not be saved. Try again.') }
  }

  return <section className="fg-connect-card fg-checklist" aria-labelledby="fg-checklist-title">
    <div className="fg-connect-card-head">
      <h2 id="fg-checklist-title">Getting started<FgHelp id="connect.checklist" /></h2>
      {checklist && <p><strong>{checklist.done} of {checklist.total}</strong> done. Steps tick themselves when FuseGrid sees them happen.</p>}
    </div>
    {error && <p className="fg-connect-error" role="alert">{error}</p>}
    {!checklist && !error && <p className="fg-connect-muted" role="status">Loading your checklist…</p>}
    {checklist && <ol className="fg-checklist-steps">
      {checklist.steps.map(step => <li key={step.step} data-step={step.step} data-done={step.done}>
        <span className="fg-checklist-mark" aria-hidden="true">{step.done ? '✓' : ''}</span>
        <span className="fg-checklist-title">{step.title}{step.done && <span className="fg-sr-only"> (done)</span>}</span>
        {!step.done && <span className="fg-checklist-actions">
          <a href={STEP_ACTIONS[step.step].href}>{STEP_ACTIONS[step.step].label}</a>
          <button type="button" onClick={() => void mark(step.step)}>Mark done</button>
        </span>}
      </li>)}
    </ol>}
  </section>
}
